import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  TableContainer,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Table,
  Stack,
  Pagination,
  styled,
  Avatar,
  Chip,
  TextField,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  IconButton,
  Tooltip,
  Slider,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import PauseIcon from "@mui/icons-material/Pause";
import DownloadIcon from "@mui/icons-material/Download";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import MicIcon from "@mui/icons-material/Mic";
import ForumIcon from "@mui/icons-material/Forum";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import axiosInstance from "../../../services/axiosInterceptors";
import apiEndPointsConfig from "../../../services/apiEndPoints";
import nipponBotApiEndpoints from "../../../services/nipponBotApiEndpoints";
import Loader from "../../../components/AnimatedLoader";
import { formatTime, getFirstLetter } from "../../../utils/common";
import NoData from "../../../components/NoData";
import CommonFilter from "../../../components/CommonFilter";
import { jwtDecode } from "jwt-decode";
import BotInterviewUpload from "./BotInterviewUpload";
import UploadFileIcon from "@mui/icons-material/UploadFile";

const LANGUAGE_MAP = {
  "hi-IN": "Hindi",
  "ur-IN": "Urdu",
  "gu-IN": "Gujarati",
  "bn-IN": "Bengali",
  "ml-IN": "Malayalam",
  "ta-IN": "Tamil",
  "te-IN": "Telugu",
  "mr-IN": "Marathi",
  "kn-IN": "Kannada",
  "pa-IN": "Punjabi",
  "en-IN": "English (Indian)",
  "or-IN": "Odia",
  "as-IN": "Assamese",
};

const TableHeading = styled(TableCell)({
  textTransform: "uppercase",
  fontWeight: 550,
  color: "#6b7280",
  fontSize: "0.7rem",
  backgroundColor: "#f9fafb",
  whiteSpace: "nowrap",
});

const statusChipSx = (status) => ({
  backgroundColor: status === "Completed" ? "#E8F5E9" : status === "Failed" ? "#FFEBEE" : status === "Rescheduled" ? "#E3F2FD" : "#FFF8E1",
  color: status === "Completed" ? "green" : status === "Failed" ? "#FF4D4F" : status === "Rescheduled" ? "#0277BD" : "#F59E0B",
  fontWeight: 600,
  borderRadius: 2,
});

const labelTextSx = {
  fontSize: "0.68rem",
  fontWeight: 700,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  color: "#6b7280",
  mb: 0.25,
};

const valueTextSx = {
  fontSize: "0.84rem",
  color: "#111827",
  wordBreak: "break-word",
};

const formatRecordingFileName = (fileName = "") => String(fileName || "-").replace(/^elevenlabs-/i, "");

const formatInterviewDateTime = (value, unixSeconds) => {
  const date = unixSeconds ? new Date(unixSeconds * 1000) : (value ? new Date(value) : null);
  if (!date || Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
};

const getInterviewDateTimeParts = (value, unixSeconds) => {
  const date = unixSeconds ? new Date(unixSeconds * 1000) : (value ? new Date(value) : null);
  if (!date || Number.isNaN(date.getTime())) return null;
  return {
    date: date.toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata", year: "numeric", month: "long", day: "numeric" }),
    time: date.toLocaleTimeString("en-IN", { timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true }),
  };
};

const getCompactDateTime = (value, unixSeconds) => {
  const date = unixSeconds ? new Date(unixSeconds * 1000) : (value ? new Date(value) : null);
  if (!date || Number.isNaN(date.getTime())) return "Date unavailable";
  return date.toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const truncateMiddle = (value = "", maxLength = 22) => {
  const text = String(value || "");
  if (text.length <= maxLength) return text;
  const edgeLength = Math.floor((maxLength - 3) / 2);
  return `${text.slice(0, edgeLength)}...${text.slice(-edgeLength)}`;
};

const getCallTitle = (row = {}) => {
  const summary = String(row?.callSummary || "").trim();
  if (summary) return summary;
  const fileName = formatRecordingFileName(row?.fileName);
  return fileName && fileName !== "-" ? fileName : "Call recording";
};

const getCandidateDisplayName = (row = {}) => {
  const name = String(row?.candidateName || row?.customerName || "").trim();
  return name || "Unknown candidate";
};

const getCallDisplayDateTime = (row = {}) => getCompactDateTime(row?.callAttemptedAt || row?.createdOn, row?.startTimeUnixSecs);

const getScheduleAgentDisplayName = (row = {}) => row?.scheduleAgentName || row?.agentName || row?.uploadedByName || "Unknown agent";

const NipponBotInteractionsTable = ({
  variant = "default",
  candidateId,
  recordingPlayerMode = "inline",
  showManualUpload = false,
  hideExport = false,
  onMessage: onMessageProp,
  onUploadComplete,
}) => {
  const token = localStorage.getItem("token");
  const decodedToken = token ? jwtDecode(token) : "";
  const userRole = decodedToken?.userRole || decodedToken?.role || "";
  const isCurator = userRole === "Curator";
  const canManualReprocess = isCurator;
  const isInterviewVariant = variant === "interviews";
  const useRecordingDialog = recordingPlayerMode === "dialog";

  const [playingId, setPlayingId] = useState(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [data, setData] = useState([]);
  const [page, setPage] = useState(1);
  const [loader, setLoader] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [paginationData, setPaginationData] = useState({});
  const [payloadData, setPayloadData] = useState();
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [isExporting, setIsExporting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [reprocessMenu, setReprocessMenu] = useState({ anchorEl: null, row: null });
  const [reprocessingRowId, setReprocessingRowId] = useState(null);
  const [retryingDownloadRowId, setRetryingDownloadRowId] = useState(null);
  const [downloadingRecordingId, setDownloadingRecordingId] = useState(null);
  const [recordingDialogRow, setRecordingDialogRow] = useState(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPage(1);
    }, 500);

  return () => clearTimeout(handler);
  }, [searchQuery]);

  useEffect(() => {
    fetchAudioDetails();
  }, [page, payloadData, debouncedSearch, candidateId]);

  const isFilter = {
    date: true,
    filter: true,
    callScore: false,
  };

  const getDateRange = () => {
    const today = new Date();
    const todayStr = today.toISOString().split("T")[0];
    const firstOfMonthStr = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split("T")[0];

    return {
      startDate: payloadData?.filters?.startDate || firstOfMonthStr,
      endDate: payloadData?.filters?.endDate || todayStr,
    };
  };

  const fetchAudioDetails = async () => {
    const { startDate, endDate } = getDateRange();
    const payload = {
      startDate,
      endDate,
      agentName: payloadData?.filters?.agents?.length > 0 ? payloadData?.filters?.agents : [],
      branchManagerName: payloadData?.filters?.branchManagers?.length > 0 ? payloadData?.filters?.branchManagers : [],
      sentiment: payloadData?.filters?.sentiment?.length > 0 ? payloadData?.filters?.sentiment : [],
      status: payloadData?.filters?.status || "",
      campaign: payloadData?.filters?.campaigns?.length > 0 ? payloadData?.filters?.campaigns : [],
      callDurationText: payloadData?.filters?.callDuration?.length > 0 ? payloadData?.filters?.callDuration : [],
      callDirection: payloadData?.filters?.callInteraction?.length > 0 ? payloadData?.filters?.callInteraction : [],
      searchTerm: debouncedSearch,
      disposition: payloadData?.filters?.disposition?.length > 0 ? payloadData?.filters?.disposition : [],
      subDisposition: payloadData?.filters?.subDisposition?.length > 0 ? payloadData?.filters?.subDisposition : [],
      frequentKeywords: payloadData?.frequentKeywords || "",
      callScore: payloadData?.filters?.callScore?.length > 0 ? payloadData?.filters?.callScore : [],
      ...(candidateId ? { candidateId } : {}),
      page,
      limit: 10,
      pagination: true,
    };

    try {
      const res = await axiosInstance.post(`${nipponBotApiEndpoints?.interactionsList}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res?.data?.data?.interactionList || []);
      setPaginationData(res?.data?.data?.paginator || {});
    } catch (error) {
      console.error("Error fetching audio details", error);
    } finally {
      setLoader(false);
    }
  };

  const fetchExportExcel = async () => {
    setIsExporting(true);
    const today = new Date();
    const payload = {
      startDate: payloadData?.filters?.startDate ? payloadData?.filters?.startDate : today?.toISOString().split("T")[0],
      endDate: payloadData?.filters?.endDate ? payloadData?.filters?.endDate : today?.toISOString().split("T")[0],
      agentName: payloadData?.filters?.agents?.length > 0 ? payloadData?.filters?.agents : [],
      sentiment: payloadData?.filters?.sentiment?.length > 0 ? payloadData?.filters?.sentiment : [],
      campaign: payloadData?.filters?.campaigns?.length > 0 ? payloadData?.filters?.campaigns : [],
      callDurationText: payloadData?.filters?.callDuration?.length > 0 ? payloadData?.filters?.callDuration : [],
      callDirection: payloadData?.filters?.callInteraction?.length > 0 ? payloadData?.filters?.callInteraction : [],
      searchTerm: debouncedSearch,
      disposition: payloadData?.filters?.disposition?.length > 0 ? payloadData?.filters?.disposition : [],
      subDisposition: payloadData?.filters?.subDisposition?.length > 0 ? payloadData?.filters?.subDisposition : [],
      frequentKeywords: payloadData?.frequentKeywords || "",
      callScore: payloadData?.filters?.callScore?.length > 0 ? payloadData?.filters?.callScore : [],
      nipponBotExport: true,
    };

    try {
      const res = await axiosInstance.post(`${apiEndPointsConfig?.exportExcelInteraction}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });

      if (!res.data) return;

      const url = window.URL.createObjectURL(
        new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" })
      );
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `interaction_export_${new Date().toISOString().split("T")[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (error) {
      console.error("Error exporting Excel file", error);
      alert("Failed to export data. Please try again.");
    } finally {
      setIsExporting(false);
    }
  };

  const handleUploadMessage = (message, severity) => {
    setSnackbar({ open: true, message, severity });
    onMessageProp?.(message, severity);
  };

  const handleUploadComplete = () => {
    setUploadDialogOpen(false);
    fetchAudioDetails();
    onUploadComplete?.();
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbar((current) => ({ ...current, open: false }));
  };

  const handleToggleCuration = async (row) => {
    const nextIsCurated = !row.isCurated;
    try {
      await axiosInstance.post(
        apiEndPointsConfig.toggleCuration,
        { recordingId: row.recordingId, isCurated: nextIsCurated },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setData((prev) => prev.map((item) => (item.recordingId === row.recordingId ? { ...item, isCurated: nextIsCurated } : item)));
    } catch (error) {
      setSnackbar({ open: true, message: "Failed to update curation status.", severity: "error" });
    }
  };

  const handleOpenReprocessMenu = (event, row) => setReprocessMenu({ anchorEl: event.currentTarget, row });
  const handleCloseReprocessMenu = () => setReprocessMenu({ anchorEl: null, row: null });

  const handleRetryDownload = async (row) => {
    if (!row?.conversationId) return;

    try {
      setRetryingDownloadRowId(row.recordingId);
      await axiosInstance.post(
        nipponBotApiEndpoints.curatorDownload,
        { conversationIds: [row.conversationId] },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSnackbar({ open: true, message: "Recording downloaded successfully. Please refresh in a moment.", severity: "success" });
      fetchAudioDetails();
    } catch (error) {
      const errorMessage = error?.response?.data?.message || "Failed to retry recording download.";
      setSnackbar({ open: true, message: errorMessage, severity: "error" });
    } finally {
      setRetryingDownloadRowId(null);
    }
  };

  const handleRetryProcessing = async (row) => {
    if (!row?.recordingId) return;

    try {
      setReprocessingRowId(row.recordingId);
      await axiosInstance.post(
        `${apiEndPointsConfig?.reprocessCallRecording}/${row.recordingId}`,
        { stage: "all" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSnackbar({ open: true, message: "Processing retry started. This may take a few minutes.", severity: "success" });
      fetchAudioDetails();
    } catch (error) {
      const errorMessage = error?.response?.data?.message || error?.response?.data?.status?.value || "Failed to retry processing.";
      setSnackbar({ open: true, message: errorMessage, severity: "error" });
    } finally {
      setReprocessingRowId(null);
    }
  };

  const handleReprocess = async (stage) => {
    const row = reprocessMenu.row;
    handleCloseReprocessMenu();
    if (!row?.recordingId) return;

    try {
      setReprocessingRowId(row.recordingId);
      await axiosInstance.post(
        `${apiEndPointsConfig?.reprocessCallRecording}/${row.recordingId}`,
        { stage },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSnackbar({ open: true, message: "Reprocessing started. This may take a few minutes.", severity: "success" });
    } catch (error) {
      const errorMessage = error?.response?.data?.status?.value || "Failed to start reprocessing. Please try again.";
      setSnackbar({ open: true, message: errorMessage, severity: "error" });
    } finally {
      setReprocessingRowId(null);
    }
  };

  const isRecordingPlayable = (row) => Boolean(row?.recordingId) && !row?.retryDownloadAvailable;

  const getRecordingStreamUrl = (row) => (
    row?.recordingId
      ? `${apiEndPointsConfig.streamAudio}/${row.recordingId}?token=${encodeURIComponent(token)}`
      : ""
  );

  const getRecordingDownloadName = (row = {}) => {
    const fileName = formatRecordingFileName(row?.fileName);
    if (fileName && fileName !== "-") return fileName;
    return row?.recordingId ? `recording-${row.recordingId}.mp3` : "recording.mp3";
  };

  const handleOpenRecordingDialog = (row) => {
    if (!isRecordingPlayable(row)) {
      setSnackbar({ open: true, message: "Recording is not available yet.", severity: "info" });
      return;
    }
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
      setPlayingId(null);
      setCurrentTime(0);
      setDuration(0);
    }
    setRecordingDialogRow(row);
  };

  const handleCloseRecordingDialog = () => {
    setRecordingDialogRow(null);
  };

  const handleRecordingAction = (row) => {
    if (useRecordingDialog) {
      handleOpenRecordingDialog(row);
      return;
    }
    handleTogglePlay(row);
  };

  const handleDownloadRecording = async (row) => {
    if (!isRecordingPlayable(row)) {
      setSnackbar({ open: true, message: "Recording is not available yet.", severity: "info" });
      return;
    }

    try {
      setDownloadingRecordingId(row.recordingId);
      const response = await axiosInstance.get(getRecordingStreamUrl(row), {
        responseType: "blob",
        headers: { Authorization: `Bearer ${token}` },
      });
      const blobUrl = window.URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = getRecordingDownloadName(row);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      setSnackbar({ open: true, message: "Failed to download recording. Please try again.", severity: "error" });
    } finally {
      setDownloadingRecordingId(null);
    }
  };

  const handleTogglePlay = (row) => {
    if (!isRecordingPlayable(row)) {
      setSnackbar({ open: true, message: "Recording is not available yet.", severity: "info" });
      return;
    }

    const src = getRecordingStreamUrl(row);

    if (!src) return;

    if (playingId === row.recordingId) {
      audioRef.current?.pause();
      setPlayingId(null);
      setCurrentTime(0);
      setDuration(0);
      return;
    }

    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.ontimeupdate = null;
      audioRef.current.onloadedmetadata = null;
      audioRef.current.onended = null;
    }

    const audio = new Audio(src);
    audioRef.current = audio;
    audio.onloadedmetadata = () => {
      setDuration(audio.duration);
      audio.playbackRate = playbackSpeed;
    };
    audio.ontimeupdate = () => setCurrentTime(audio.currentTime);
    audio.onended = () => {
      setPlayingId(null);
      setCurrentTime(0);
      setDuration(0);
    };
    audio.onerror = () => {
      setPlayingId(null);
      setCurrentTime(0);
      setDuration(0);
      setSnackbar({ open: true, message: "Recording is not available yet.", severity: "info" });
    };

    audio.play().catch(() => {
      setPlayingId(null);
      setCurrentTime(0);
      setDuration(0);
      setSnackbar({ open: true, message: "Recording is not available yet.", severity: "info" });
    });
    setPlayingId(row.recordingId);
    setCurrentTime(0);
    setDuration(0);
  };

  const getProcessingLabel = (row) => (
    row?.displayStatus
      ? row.displayStatus
      : row?.transcriptionStatus === "Completed"
      ? "Completed"
      : row?.transcriptionStatus === "Failed"
        ? "Failed"
        : "In Progress"
  );

  const renderMobileField = (label, value) => (
    <Box>
      <Typography sx={labelTextSx}>{label}</Typography>
      <Typography sx={{ ...valueTextSx, whiteSpace: "pre-line" }}>{value || "-"}</Typography>
    </Box>
  );

  return (
    <Box sx={{ minWidth: 0, width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
      {loader ? (
        <Loader />
      ) : (
        <Box sx={{ p: { xs: 1, sm: 1.5, md: 3 }, backgroundColor: "#F8F8FA", minWidth: 0, width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
          <Box
            sx={{
              borderRadius: 2.5,
              display: 'flex',
              flexDirection: { xs: 'column', lg: 'row' },
              justifyContent: 'space-between',
              alignItems: { xs: 'stretch', lg: 'center' },
              gap: { xs: 1.25, md: 2 },
              flexWrap: 'wrap',
              minWidth: 0,
              maxWidth: '100%',
            }}
          >
            <Box sx={{ width: { xs: '100%', lg: 340 }, minWidth: 0, flexShrink: 0 }}>
              <TextField
                size="small"
                variant="outlined"
                placeholder="Search candidate, SAP code, mobile, file, or call ID..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPage(1);
                }}
                sx={{
                  width: "100%",
                  backgroundColor: "#fff",
                  "& .MuiOutlinedInput-root": {
                    "&:hover fieldset": { borderColor: "#6366F1" },
                    "&.Mui-focused fieldset": { borderColor: "#6366F1" },
                    "& .MuiInputBase-input": { fontSize: 14 },
                    borderRadius: 2,
                    p: "5px 20px",
                  },
                }}
              />
            </Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: { xs: 'stretch', lg: 'flex-end' },
                gap: 1.25,
                alignItems: { xs: 'stretch', lg: 'center' },
                flexWrap: 'wrap',
                ml: { xs: 0, lg: 'auto' },
                width: { xs: '100%', lg: 'auto' },
                maxWidth: '100%',
                minWidth: 0,
              }}
            >
              <Box sx={{ minWidth: 0, maxWidth: '100%', flex: { xs: '1 1 100%', lg: '1 1 auto' }, width: { xs: '100%', lg: 'auto' } }}>
                  <CommonFilter
                  storageName={isInterviewVariant ? "nipponBotInterviewQueueFilter" : "nipponBotInteractionFilter"}
                  setPayloadData={setPayloadData}
                  isFilter={isFilter}
                  defaultDateRange="month"
                />
              </Box>
              {showManualUpload && (
                <Button
                  variant="outlined"
                  startIcon={<UploadFileIcon />}
                  onClick={() => setUploadDialogOpen(true)}
                  sx={{
                    height: "35px",
                    width: { xs: '100%', sm: 'fit-content' },
                    flexShrink: 0,
                    alignSelf: { xs: 'stretch', sm: 'flex-start', lg: 'auto' },
                    textTransform: "none",
                    color: "#7635dd",
                    borderColor: "#7635dd",
                    borderRadius: "12px",
                    "&:hover": { borderColor: "#5f2ab8", backgroundColor: "rgba(118,53,221,0.08)" },
                  }}
                >
                  Manual Upload
                </Button>
              )}
              {!hideExport && (
                <Button
                  variant="contained"
                  color="primary"
                  disabled={isExporting}
                  sx={{
                    height: "35px",
                    width: { xs: '100%', sm: 'fit-content', lg: '130px' },
                    minWidth: { xs: 0, sm: 120 },
                    flexShrink: 0,
                    alignSelf: { xs: 'stretch', sm: 'flex-start', lg: 'auto' },
                    backgroundColor: isInterviewVariant ? "#0284c7" : "#7635dd",
                    borderRadius: isInterviewVariant ? "14px" : "12px",
                    boxShadow: isInterviewVariant ? "0 10px 24px rgba(2, 132, 199, 0.24)" : "none",
                    "&:hover": { backgroundColor: isInterviewVariant ? "#0369a1" : "#5f2ab8" },
                    "&:disabled": { backgroundColor: isInterviewVariant ? "#7dd3fc" : "#c4b5e0" },
                  }}
                  onClick={fetchExportExcel}
                  startIcon={isExporting ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : null}
                >
                  {isExporting ? "Exporting..." : isInterviewVariant ? "Export Interview Queue" : "Export"}
                </Button>
              )}
            </Box>
          </Box>
          <Box sx={{ display: { xs: "grid", md: "none" }, gap: 1.5, mt: 3 }}>
            {data?.length === 0 ? (
              <Paper
                sx={{
                  p: 2,
                  borderRadius: 3,
                  border: isInterviewVariant ? "1px solid #cfe2f7" : "1px solid #EEEEEE",
                }}
              >
                <NoData />
              </Paper>
            ) : (
              data.map((row) => {
                const processingLabel = getProcessingLabel(row);
                const titleValue = isInterviewVariant ? (row?.agentName || row?.uploadedByName || "-") : getCallDisplayDateTime(row);
                const subtitleValue = isInterviewVariant ? (row?.conversationId || "-") : getCandidateDisplayName(row);
                const secondaryValue = isInterviewVariant
                  ? (Number.isFinite(Number(row?.callDurationSecs)) ? formatTime(Number(row.callDurationSecs)) : "-")
                  : (row?.mobileNumber || "-");

                return (
                  <Paper
                    key={row.recordingId || row.transcriptionId || row.conversationId || row.fileName}
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      border: isInterviewVariant ? "1px solid #cfe2f7" : "1px solid #EEEEEE",
                      boxShadow: "none",
                    }}
                  >
                    <Stack spacing={1.5}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
                        <Avatar
                          sx={{
                            bgcolor: isInterviewVariant ? "#0ea5e9" : "#7635DD",
                            width: 34,
                            height: 34,
                            fontSize: 13,
                            flexShrink: 0,
                          }}
                        >
                          {getFirstLetter(titleValue) || "B"}
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ ...valueTextSx, fontWeight: 700 }}>{titleValue}</Typography>
                          <Typography sx={{ fontSize: "0.76rem", color: "#6b7280", wordBreak: "break-all" }}>{subtitleValue}</Typography>
                        </Box>
                      </Box>

                      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 1.25 }}>
                        {renderMobileField(isInterviewVariant ? "Date & Time" : "Call Date", isInterviewVariant ? formatInterviewDateTime(row?.createdOn, row?.startTimeUnixSecs) : getCallDisplayDateTime(row))}
                        {renderMobileField(
                          "Candidate",
                          getCandidateDisplayName(row)
                        )}
                        {!isInterviewVariant && renderMobileField("SAP Code", row?.sapCode || "-")}
                        {isInterviewVariant && renderMobileField("Duration", secondaryValue)}
                        {!isInterviewVariant && renderMobileField("Agent", getScheduleAgentDisplayName(row))}
                        {!isInterviewVariant && renderMobileField("Conversation / Agent ID", `${row?.conversationId ? truncateMiddle(row.conversationId, 28) : "-"}\n${row?.elevenLabsAgentId || row?.agentId ? truncateMiddle(row.elevenLabsAgentId || row.agentId, 28) : "-"}`)}
                        {isInterviewVariant && renderMobileField("Language", LANGUAGE_MAP[row?.language] || row?.language || "-")}
                      </Box>

                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                        <Chip label={processingLabel} size="small" sx={statusChipSx(processingLabel)} />
                        {row?.retryDownloadAvailable && !isInterviewVariant && (
                          <Chip
                            label={row?.retryReason === "missing_file" ? "Recording file missing" : row?.retryReason === "empty_audio" ? "Recording file empty" : "Retry Recording"}
                            size="small"
                            sx={{ backgroundColor: "#FEF3C7", color: "#92400E", fontWeight: 600, borderRadius: 2, maxWidth: 180 }}
                          />
                        )}
                      </Box>

                      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                        {isRecordingPlayable(row) ? (
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => handleRecordingAction(row)}
                            sx={{
                              textTransform: "none",
                              color: "#7635DD",
                              borderColor: "#7635DD",
                              "&:hover": { borderColor: "#7635DD", backgroundColor: "rgba(118,53,221,0.08)" },
                            }}
                          >
                            {useRecordingDialog ? "Play" : playingId === row.recordingId ? "Pause" : "Play"}
                          </Button>
                        ) : (
                          <Typography fontSize="0.75rem" color="text.secondary">Recording not available yet</Typography>
                        )}
                        <Button
                          variant="outlined"
                          size="small"
                          disabled={!row?.reportReady}
                          onClick={() => window.open('/user/audio-analytics/' + row.transcriptionId, '_blank')}
                          sx={{ textTransform: "none", color: "#7635DD", borderColor: "#7635DD" }}
                        >
                          View Report
                        </Button>
                        {row?.retryDownloadAvailable && !isInterviewVariant && (
                          <Button
                            variant="text"
                            size="small"
                            disabled={retryingDownloadRowId === row.recordingId}
                            onClick={() => handleRetryDownload(row)}
                            sx={{ textTransform: "none", color: "#b45309" }}
                          >
                            {retryingDownloadRowId === row.recordingId ? "Retrying..." : "Retry Recording"}
                          </Button>
                        )}
                        {canManualReprocess && (
                          <Button
                            variant="text"
                            size="small"
                            disabled={!row?.recordingId || reprocessingRowId === row.recordingId}
                            onClick={(event) => handleOpenReprocessMenu(event, row)}
                            sx={{ textTransform: "none", color: "#7635DD" }}
                          >
                            Reprocess
                          </Button>
                        )}
                      </Stack>
                    </Stack>
                  </Paper>
                );
              })
            )}
          </Box>

          <TableContainer
            component={Paper}
            sx={{
              display: { xs: "none", md: "block" },
              border: isInterviewVariant ? "1px solid #cfe2f7" : "1px solid #EEEEEE",
              borderRadius: isInterviewVariant ? 4 : 2,
              overflowX: 'auto',
              overflowY: 'hidden',
              WebkitOverflowScrolling: 'touch',
              maxWidth: '100%',
              width: '100%',
              boxSizing: 'border-box',
              minWidth: 0,
              mt: 3,
              background: isInterviewVariant ? "linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)" : '#fff',
            }}
          >
            <Table
              sx={{
                minWidth: isInterviewVariant ? 1280 : 1060,
                width: isInterviewVariant ? 'max-content' : '100%',
                tableLayout: isInterviewVariant ? 'auto' : 'fixed',
                ...(!isInterviewVariant
                  ? {
                      "& .MuiTableCell-root": {
                        px: 1.25,
                        py: 1.15,
                      },
                    }
                  : {}),
              }}
            >
              <TableHead sx={isInterviewVariant ? { backgroundColor: "#e0f2fe" } : undefined}>
                <TableRow>
                  {isInterviewVariant ? (
                    <>
                      <TableHeading>Agent Name</TableHeading>
                      <TableHeading>Conversation ID</TableHeading>
                      <TableHeading>Call Duration</TableHeading>
                      <TableHeading>Date & Time</TableHeading>
                      <TableHeading sx={{ textAlign: "center" }}>Main Language</TableHeading>
                      <TableHeading>Candidate Name</TableHeading>
                      <TableHeading sx={{ textAlign: "center" }}>Processing Status</TableHeading>
                      <TableHeading sx={{ textAlign: "center" }}>Action</TableHeading>
                      <TableHeading sx={{ textAlign: "center" }}>Report</TableHeading>
                      {isCurator && <TableHeading sx={{ textAlign: "center" }}>Curate</TableHeading>}
                      {canManualReprocess && <TableHeading sx={{ textAlign: "center" }}>Reprocess</TableHeading>}
                    </>
                  ) : (
                    <>
                      <TableHeading sx={{ width: 165 }}>Call Date</TableHeading>
                      <TableHeading sx={{ width: 190 }}>Candidate Name</TableHeading>
                      <TableHeading sx={{ width: 120 }}>SAP Code</TableHeading>
                      <TableHeading sx={{ width: 175 }}>Agent Name</TableHeading>
                      <TableHeading sx={{ width: 235 }}>Conversation / Agent ID</TableHeading>
                      <TableHeading sx={{ textAlign: "center", width: 125 }}>Status</TableHeading>
                      <TableHeading sx={{ textAlign: "center", width: 170 }}>Actions</TableHeading>
                    </>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {data?.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={isInterviewVariant ? (9 + (isCurator ? 1 : 0) + (canManualReprocess ? 1 : 0)) : 7} align="center">
                      <NoData />
                    </TableCell>
                  </TableRow>
                ) : (
                  data.map((row) => (
                    <TableRow
                      key={row.recordingId || row.transcriptionId || row.conversationId || row.fileName}
                      hover
                      sx={{ "&:hover": { backgroundColor: isInterviewVariant ? "#f0f9ff" : "#faf7ff" } }}
                    >
                      {isInterviewVariant ? (
                        <>
                          <TableCell sx={{ fontSize: "0.8rem", fontWeight: 550, whiteSpace: "nowrap" }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Avatar sx={{ bgcolor: "#0ea5e9", width: 28, height: 28, fontSize: 12 }}>
                                {getFirstLetter(row?.agentName || row?.uploadedByName) || "B"}
                              </Avatar>
                              {row?.agentName || row?.uploadedByName || "-"}
                            </Box>
                          </TableCell>
                          <TableCell sx={{ fontSize: "0.8rem", color: "#666666", whiteSpace: "nowrap" }}>{row?.conversationId || "-"}</TableCell>
                          <TableCell sx={{ fontSize: "0.8rem", color: "#666666", whiteSpace: "nowrap" }}>
                            {Number.isFinite(Number(row?.callDurationSecs)) ? formatTime(Number(row.callDurationSecs)) : "-"}
                          </TableCell>
                          <TableCell sx={{ fontSize: "0.8rem", color: "#666666", whiteSpace: "nowrap", minWidth: 220 }}>
                            {formatInterviewDateTime(row?.createdOn, row?.startTimeUnixSecs)}
                          </TableCell>
                          <TableCell sx={{ textAlign: "center", fontSize: "0.8rem", color: "#666666", minWidth: 110 }}>{LANGUAGE_MAP[row?.language] || row?.language || "-"}</TableCell>
                          <TableCell sx={{ fontSize: "0.8rem", color: "#666666", whiteSpace: "nowrap" }}>{row?.candidateName || row?.customerName || "-"}</TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell sx={{ width: 165, maxWidth: 165 }}>
                            <Tooltip title={formatInterviewDateTime(row?.callAttemptedAt || row?.createdOn, row?.startTimeUnixSecs)}>
                              <Box sx={{ minWidth: 0 }}>
                                <Typography sx={{ fontSize: "0.84rem", fontWeight: 700, color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                  {getCallDisplayDateTime(row)}
                                </Typography>
                              </Box>
                            </Tooltip>
                          </TableCell>
                          <TableCell sx={{ width: 190, maxWidth: 190 }}>
                            <Typography sx={{ fontSize: "0.84rem", fontWeight: 700, color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {getCandidateDisplayName(row)}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ width: 120, maxWidth: 120 }}>
                            <Typography sx={{ fontSize: "0.84rem", fontWeight: 700, color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {row?.sapCode || "-"}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ width: 175, maxWidth: 175 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                              <Avatar sx={{ bgcolor: "#7635DD", width: 30, height: 30, fontSize: 12, flexShrink: 0 }}>{getFirstLetter(getScheduleAgentDisplayName(row)) || "B"}</Avatar>
                              <Box sx={{ minWidth: 0 }}>
                                <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                  {getScheduleAgentDisplayName(row)}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ width: 235, maxWidth: 235 }}>
                            <Tooltip title={[row?.conversationId, row?.elevenLabsAgentId || row?.agentId].filter(Boolean).join("\n") || ""}>
                              <Box sx={{ minWidth: 0 }}>
                                <Typography sx={{ fontSize: "0.82rem", color: "#111827", fontFamily: "monospace", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                  {row?.conversationId ? truncateMiddle(row.conversationId, 32) : "-"}
                                </Typography>
                                <Typography sx={{ fontSize: "0.72rem", color: "#6b7280", fontFamily: "monospace", mt: 0.25, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                  {row?.elevenLabsAgentId || row?.agentId ? truncateMiddle(row.elevenLabsAgentId || row.agentId, 32) : "-"}
                                </Typography>
                              </Box>
                            </Tooltip>
                            <Typography sx={{ fontSize: "0.72rem", color: "#9ca3af", mt: 0.25 }}>
                              {LANGUAGE_MAP[row?.language] || row?.language || ""}
                            </Typography>
                          </TableCell>
                        </>
                      )}

                      <TableCell sx={{ textAlign: "center", width: isInterviewVariant ? 135 : 125, maxWidth: isInterviewVariant ? 135 : 125 }}>
                        <Stack direction="column" spacing={0.75} alignItems="center">
                          <Chip
                            label={getProcessingLabel(row)}
                            size="small"
                            sx={{
                              ...statusChipSx(getProcessingLabel(row)),
                            }}
                          />
                          {row?.retryDownloadAvailable && !isInterviewVariant && (
                            <Chip
                              label={row?.retryReason === "missing_file" ? "Recording file missing" : row?.retryReason === "empty_audio" ? "Recording file empty" : "Retry Recording"}
                              size="small"
                              sx={{ backgroundColor: "#FEF3C7", color: "#92400E", fontWeight: 600, borderRadius: 2, maxWidth: 180 }}
                            />
                          )}
                        </Stack>
                      </TableCell>

                      <TableCell sx={{ textAlign: "center", width: isInterviewVariant ? 180 : 170, maxWidth: isInterviewVariant ? 180 : 170 }}>
                        {isRecordingPlayable(row) ? (
                          !useRecordingDialog && playingId === row.recordingId ? (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, bgcolor: "#f8f9fa", p: "6px 8px", borderRadius: 2, width: 180 }}>
                              <IconButton size="small" onClick={() => handleTogglePlay(row)} sx={{ bgcolor: isInterviewVariant ? "#0284c7" : "#7635DD", color: "white", "&:hover": { bgcolor: isInterviewVariant ? "#0369a1" : "#5f2bb3" }, width: 28, height: 28, flexShrink: 0 }}>
                                <PauseIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                              <Slider min={0} max={duration || 0} value={currentTime} onChange={(e, val) => { if (audioRef.current) { audioRef.current.currentTime = val; setCurrentTime(val); } }} sx={{ flex: 1, color: isInterviewVariant ? "#0284c7" : "#7635DD", py: "6px" }} size="small" />
                              <Typography variant="caption" sx={{ whiteSpace: "nowrap", flexShrink: 0, fontSize: "0.62rem", color: "#555" }}>{formatTime(currentTime)}</Typography>
                              <Box sx={{ display: "flex", gap: 0.3, flexShrink: 0 }}>
                                {[1, 2].map((speed) => (
                                  <Button key={speed} onClick={() => { setPlaybackSpeed(speed); if (audioRef.current) audioRef.current.playbackRate = speed; }} sx={{ minWidth: "28px", height: "18px", p: "1px 3px", fontSize: "9px", fontWeight: 700, borderRadius: "4px", bgcolor: playbackSpeed === speed ? "#7635DD" : "transparent", color: playbackSpeed === speed ? "white" : "#7635DD", border: "1px solid #7635DD", textTransform: "none", "&:hover": { bgcolor: playbackSpeed === speed ? "#5f2bb3" : "rgba(118,53,221,0.08)", border: "1px solid #7635DD" } }}>{speed}x</Button>
                                ))}
                              </Box>
                            </Box>
                          ) : (
                            <Tooltip title="Play Audio">
                              <IconButton size="small" onClick={() => handleRecordingAction(row)} sx={{ color: isInterviewVariant ? "#0284c7" : "#7C3AED" }}>
                                <PlayCircleIcon sx={{ fontSize: 28 }} />
                              </IconButton>
                            </Tooltip>
                          )
                        ) : <Typography fontSize="0.75rem" color="text.secondary">Recording not available yet</Typography>}
                        {!isInterviewVariant && row?.retryDownloadAvailable && (
                          <Button
                            variant="text"
                            size="small"
                            disabled={retryingDownloadRowId === row.recordingId}
                            onClick={() => handleRetryDownload(row)}
                            sx={{ mt: 0.5, fontSize: "0.7rem", textTransform: "none", color: "#b45309" }}
                          >
                            {retryingDownloadRowId === row.recordingId ? "Retrying..." : "Retry Recording"}
                          </Button>
                        )}
                        {!isInterviewVariant && (
                          <Button
                            variant="outlined"
                            size="small"
                            disabled={!row?.reportReady}
                            onClick={() => window.open('/user/audio-analytics/' + row.transcriptionId, '_blank')}
                            sx={{ mt: 0.5, fontSize: "0.7rem", textTransform: "none", color: "#7635DD", borderColor: "#7635DD", "&:disabled": { borderColor: "#ccc", color: "#ccc" } }}
                          >
                            View Report
                          </Button>
                        )}
                        {!isInterviewVariant && isCurator && (
                          <Button
                            variant="text"
                            size="small"
                            onClick={() => handleToggleCuration(row)}
                            sx={{ mt: 0.5, fontSize: "0.7rem", textTransform: "none", color: "#7635DD" }}
                          >
                            {row?.isCurated ? "Curated" : "Curate"}
                          </Button>
                        )}
                        {!isInterviewVariant && canManualReprocess && (
                          <Button
                            variant="text"
                            size="small"
                            disabled={!row?.recordingId || reprocessingRowId === row.recordingId}
                            onClick={(event) => handleOpenReprocessMenu(event, row)}
                            sx={{ mt: 0.5, fontSize: "0.7rem", textTransform: "none", color: "#7635DD" }}
                          >
                            Reprocess
                          </Button>
                        )}
                      </TableCell>

                      {isInterviewVariant && <TableCell sx={{ textAlign: "center", minWidth: 130 }}>
                        <Tooltip title="">
                          <span>
                            <Button
                              variant="outlined"
                              size="small"
                              disabled={!row?.reportReady}
                              onClick={() => window.open('/user/audio-analytics/' + row.transcriptionId, '_blank')}
                              sx={{
                                borderRadius: 2,
                                color: "#7635DD",
                                borderColor: "#7635DD",
                                fontSize: "0.75rem",
                                textTransform: "none",
                                "&:hover": { backgroundColor: isInterviewVariant ? "#e0f2fe" : "#F4EFFC" },
                                "&:disabled": { borderColor: "#ccc", color: "#ccc" },
                              }}
                            >
                              View Report
                            </Button>
                          </span>
                        </Tooltip>
                      </TableCell>}

                      {isInterviewVariant && isCurator && (
                        <TableCell sx={{ textAlign: "center" }}>
                          <Button
                            variant={row?.isCurated ? "contained" : "outlined"}
                            size="small"
                            onClick={() => handleToggleCuration(row)}
                            sx={{
                              borderRadius: 5,
                              fontSize: "0.7rem",
                              fontWeight: 600,
                              textTransform: "uppercase",
                              px: 2,
                              ...(row?.isCurated ? { backgroundColor: "#7635DD", color: "#fff", "&:hover": { backgroundColor: "#5f2bb3" } } : { color: "#666666", borderColor: "#cccccc", "&:hover": { backgroundColor: "#F4EFFC", borderColor: "#7635DD" } }),
                            }}
                          >
                            {row?.isCurated ? "Curated" : "Curate"}
                          </Button>
                        </TableCell>
                      )}

                      {isInterviewVariant && canManualReprocess && (
                        <TableCell sx={{ textAlign: "center" }}>
                          <Stack direction="column" spacing={0.75} alignItems="center">
                            <Button
                              variant="outlined"
                              size="small"
                              disabled={!row?.recordingId || reprocessingRowId === row.recordingId}
                              onClick={(event) => handleOpenReprocessMenu(event, row)}
                              startIcon={reprocessingRowId === row.recordingId ? <CircularProgress size={14} sx={{ color: "#7635DD" }} /> : <AutorenewIcon sx={{ fontSize: 16 }} />}
                              sx={{
                                borderRadius: 5,
                                fontSize: "0.7rem",
                                fontWeight: 600,
                                textTransform: "none",
                                color: "#7635DD",
                                borderColor: "#7635DD",
                                "&:hover": { backgroundColor: "#F4EFFC", borderColor: "#7635DD" },
                              }}
                            >
                              Reprocess
                            </Button>
                            {!isInterviewVariant && row?.retryProcessingAvailable && (
                              <Button
                                variant="contained"
                                size="small"
                                disabled={reprocessingRowId === row.recordingId}
                                onClick={() => handleRetryProcessing(row)}
                                sx={{ borderRadius: 5, fontSize: "0.68rem", fontWeight: 700, textTransform: "none", backgroundColor: "#dc2626", "&:hover": { backgroundColor: "#b91c1c" } }}
                              >
                                {reprocessingRowId === row.recordingId ? "Retrying..." : "Retry Failed Processing"}
                              </Button>
                            )}
                          </Stack>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Stack spacing={2} sx={{ mt: 2, display: "flex", alignItems: "flex-end", p: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {paginationData?.itemCount > 0
                ? `Showing ${(paginationData?.currentPage - 1) * paginationData?.perPage + 1}-${Math.min(
                    paginationData?.currentPage * paginationData?.perPage,
                    paginationData?.itemCount
                  )} of ${paginationData?.itemCount} results`
                : "No results found"}
            </Typography>
            <Pagination
              count={paginationData?.pageCount || 1}
              page={paginationData?.currentPage || 1}
              onChange={(event, value) => setPage(value)}
              sx={{
                "& .MuiPaginationItem-root": { color: isInterviewVariant ? "#0284c7" : "#7635DD", borderColor: isInterviewVariant ? "#0284c7" : "#7635DD" },
                "& .MuiPaginationItem-root.Mui-selected": { backgroundColor: `${isInterviewVariant ? "#0284c7" : "#7635DD"} !important`, color: "#fff !important" },
                "& .MuiPaginationItem-root:hover": { backgroundColor: isInterviewVariant ? "#e0f2fe" : "#E0E7FF" },
              }}
            />
          </Stack>
        </Box>
      )}
      <Menu
        anchorEl={reprocessMenu.anchorEl}
        open={Boolean(reprocessMenu.anchorEl)}
        onClose={handleCloseReprocessMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem onClick={() => handleReprocess("transcription")}>
          <ListItemIcon><MicIcon fontSize="small" sx={{ color: "#7635DD" }} /></ListItemIcon>
          <ListItemText>Reprocess Transcription</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleReprocess("interaction")}>
          <ListItemIcon><ForumIcon fontSize="small" sx={{ color: "#7635DD" }} /></ListItemIcon>
          <ListItemText>Reprocess Interaction</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleReprocess("callAdherence")}>
          <ListItemIcon><FactCheckIcon fontSize="small" sx={{ color: "#7635DD" }} /></ListItemIcon>
          <ListItemText>Reprocess Call Adherence</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleReprocess("all")}>
          <ListItemIcon><DoneAllIcon fontSize="small" sx={{ color: "#7635DD" }} /></ListItemIcon>
          <ListItemText>Reprocess All</ListItemText>
        </MenuItem>
      </Menu>
      <Dialog
        open={Boolean(recordingDialogRow)}
        onClose={handleCloseRecordingDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: "1px solid #eceff3",
            boxShadow: "0 24px 70px rgba(17, 24, 39, 0.18)",
          },
        }}
      >
        <DialogTitle sx={{ pb: 1.5 }}>
          <Typography sx={{ fontSize: "1.1rem", fontWeight: 700, color: "#111827" }}>
            Recording Player
          </Typography>
          <Typography sx={{ mt: 0.5, fontSize: "0.82rem", color: "#6b7280", wordBreak: "break-word" }}>
            {recordingDialogRow ? getCallTitle(recordingDialogRow) : "-"}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Stack spacing={2}>
            <Box
              sx={{
                border: "1px solid #eceff3",
                borderRadius: 2,
                p: 2,
                backgroundColor: "#fafafa",
              }}
            >
              <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: "#6b7280", textTransform: "uppercase", mb: 0.75 }}>
                File
              </Typography>
              <Typography sx={{ fontSize: "0.9rem", fontWeight: 600, color: "#111827", wordBreak: "break-all" }}>
                {recordingDialogRow ? getRecordingDownloadName(recordingDialogRow) : "-"}
              </Typography>
              <Typography sx={{ mt: 0.5, fontSize: "0.78rem", color: "#6b7280" }}>
                {recordingDialogRow ? getCompactDateTime(recordingDialogRow?.createdOn, recordingDialogRow?.startTimeUnixSecs) : "-"}
              </Typography>
            </Box>
            {recordingDialogRow && (
              <Box
                component="audio"
                controls
                autoPlay
                src={getRecordingStreamUrl(recordingDialogRow)}
                sx={{
                  width: "100%",
                  display: "block",
                  outline: "none",
                }}
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, gap: 1, flexWrap: "wrap" }}>
          <Button
            onClick={handleCloseRecordingDialog}
            variant="outlined"
            sx={{
              textTransform: "none",
              color: "#7635DD",
              borderColor: "#7635DD",
              "&:hover": { borderColor: "#7635DD", backgroundColor: "rgba(118,53,221,0.08)" },
            }}
          >
            Close
          </Button>
          {recordingDialogRow && (
            <Button
              onClick={() => handleDownloadRecording(recordingDialogRow)}
              disabled={downloadingRecordingId === recordingDialogRow.recordingId}
              variant="contained"
              startIcon={downloadingRecordingId === recordingDialogRow.recordingId ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <DownloadIcon />}
              sx={{
                textTransform: "none",
                backgroundColor: "#7635DD",
                "&:hover": { backgroundColor: "#5f2bb3" },
                "&:disabled": { backgroundColor: "#c4b5e0", color: "#fff" },
              }}
            >
              {downloadingRecordingId === recordingDialogRow.recordingId ? "Downloading..." : "Download Recording"}
            </Button>
          )}
        </DialogActions>
      </Dialog>
      <Dialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: "1px solid #eceff3",
            boxShadow: "0 24px 70px rgba(17, 24, 39, 0.18)",
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography sx={{ fontSize: "1.1rem", fontWeight: 700, color: "#111827" }}>
            Manual Upload
          </Typography>
          <Typography sx={{ mt: 0.5, fontSize: "0.82rem", color: "#6b7280" }}>
            Upload one or more interview recordings for analysis.
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <BotInterviewUpload onComplete={handleUploadComplete} onMessage={handleUploadMessage} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => setUploadDialogOpen(false)}
            variant="outlined"
            sx={{
              textTransform: "none",
              color: "#7635DD",
              borderColor: "#7635DD",
              "&:hover": { borderColor: "#7635DD", backgroundColor: "rgba(118,53,221,0.08)" },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: "top", horizontal: "right" }}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%", boxShadow: 3 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default NipponBotInteractionsTable;




